import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { ReportThis } from "./ReportThis.tsx";
import { Button } from "./souvenir.ts";

// In a modal, Tab and Shift+Tab go round what can take the focus in `el` and never leave it, whatever had
// the focus: what's under a modal is out of reach.
export function tabWithin(el: HTMLElement, e: KeyboardEvent) {
  if (e.key !== "Tab") return;
  const stops = [
    ...el.querySelectorAll<HTMLElement>(
      "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled)",
    ),
  ];
  const start = stops[0];
  const last = stops.at(-1);
  if (!start || !last) return;
  const at = document.activeElement;
  if (e.shiftKey && (at === start || !el.contains(at))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (at === last || !el.contains(at))) {
    e.preventDefault();
    start.focus();
  }
}

// A modal dialog over a dimmed page. The page under it can't be reached while it's open: Tab stays
// inside it, Escape is Cancel (and closes nothing under it), focus starts on `focus` (a selector inside
// it) and goes back where it was when it closes. `onCancel` is ignored while `busy`. Another opened over it
// (the feedback form, from Report this in its error) takes the keys until it closes.
export function Modal({
  title,
  children,
  busy,
  focus,
  onCancel,
  className,
}: {
  title: string;
  // The body, then whatever the dialog ends with (its buttons): `describedBy` names the part that says
  // what will happen.
  children: (ids: { describedBy: string }) => ReactNode;
  busy: boolean;
  focus: string;
  onCancel: () => void;
  className?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const bodyId = useId();
  // The latest handler, for the key listener set up once.
  const cancel = useRef(onCancel);
  cancel.current = busy ? () => {} : onCancel;
  const first = useRef(focus);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    el.showModal();
    el.querySelector<HTMLElement>(first.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      // Only the dialog on top answers: another opened over this one (Report this, from its error) has the keys.
      const open = document.querySelectorAll("dialog[open]");
      if (open[open.length - 1] !== el) return;
      if (e.key === "Escape") {
        // Cancel only: not the card or view under it.
        e.preventDefault();
        e.stopImmediatePropagation();
        cancel.current();
      } else tabWithin(el, e);
    };
    addEventListener("keydown", onKey, true);
    return () => {
      removeEventListener("keydown", onKey, true);
      el.close();
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialog}
      className={`tm-dialog${className ? ` ${className}` : ""}`}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      // The browser's own Escape is handled above, as Cancel.
      onCancel={(e) => e.preventDefault()}
    >
      <h2 id={titleId} className="title">
        {title}
      </h2>
      {children({ describedBy: bodyId })}
    </dialog>,
    document.body,
  );
}

// Asking first: what will happen, with Cancel and the action. Before something that can't simply be taken back
// (deleting a trip, leaving it) the action is in red; before what Updates can undo (Remove from trip: DESIGN.md,
// "Remove from trip") it's the usual primary button (`undoable`). Focus starts on Cancel.
export function ConfirmDialog({
  title,
  children,
  action,
  doing,
  undoable = false,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  // The action's words, and what it says while it works ("Delete trip", "Deleting…").
  action: string;
  doing: string;
  undoable?: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal title={title} busy={busy} focus=".tm-dialog-cancel" onCancel={onCancel}>
      {({ describedBy }) => (
        <>
          <div id={describedBy} className="tm-dialog-body">
            {children}
          </div>
          {error && (
            <p className="callout tm-error" role="alert">
              {error}
              <ReportThis />
            </p>
          )}
          <div className="tm-dialog-actions">
            <Button variant="secondary" className="tm-dialog-cancel" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant={undoable ? "primary" : "secondary"}
              {...(undoable ? {} : { className: "tm-danger-button" })}
              disabled={busy}
              onClick={onConfirm}
            >
              {busy ? doing : action}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
